/**
 * Handler capacity calculation utilities
 * Single source of truth for calculating handler open slots
 */

/**
 * Calculate the number of open slots for a handler based on approved requests
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests (approved/pending/cancelled)
 * @returns {number} Number of open slots available
 */
export const calculateHandlerOpenSlots = (handler, allRequests = []) => {
  if (!handler?.handlerInfo) {
    return 0;
  }

  const totalBirds = handler.handlerInfo.totalBirds || 0;
  const reserved = handler.handlerInfo.reserved || 0;

  // Count approved requests for this handler
  const approvedRequests = allRequests.filter(request => 
    request.handlerId === handler.id && 
    request.status === 'approved'
  );

  // Calculate total approved birds
  const approvedBirds = approvedRequests.reduce((total, request) => {
    return total + (request.numberOfBirds || request.birdsRequested || 0);
  }, 0);

  // Calculate open slots: totalBirds - reserved - approvedBirds
  return Math.max(0, totalBirds - reserved - approvedBirds);
};

/**
 * Calculate how many more birds a handler can accept from a specific breeder
 * Takes into account the 5-bird limit per handler per breeder
 * @param {Object} handler - Handler participant object
 * @param {string} breederId - ID of the breeder
 * @param {Array} allRequests - All handler requests
 * @returns {number} Number of birds this handler can still accept from this breeder
 */
export const calculateAvailableSlotsForBreeder = (handler, breederId, allRequests = []) => {
  const openSlots = calculateHandlerOpenSlots(handler, allRequests);
  
  if (openSlots <= 0) {
    return 0;
  }

  // Count approved birds this handler already has from this breeder
  const approvedRequestsFromBreeder = allRequests.filter(request => 
    request.handlerId === handler.id && 
    request.breederId === breederId && 
    request.status === 'approved'
  );

  const currentBirdsFromBreeder = approvedRequestsFromBreeder.reduce((total, request) => {
    return total + (request.numberOfBirds || request.birdsRequested || 0);
  }, 0);

  // Handler can accept max 5 birds per breeder
  const remainingBreederSlots = Math.max(0, 5 - currentBirdsFromBreeder);

  // Return the minimum of open slots and remaining breeder slots
  return Math.min(openSlots, remainingBreederSlots);
};

/**
 * Check if a handler is at full capacity
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests
 * @returns {boolean} True if handler is at capacity
 */
export const isHandlerAtCapacity = (handler, allRequests = []) => {
  return calculateHandlerOpenSlots(handler, allRequests) <= 0;
};

/**
 * Get handler capacity summary for display
 * @param {Object} handler - Handler participant object
 * @param {Array} allRequests - All handler requests
 * @returns {Object} Capacity summary with totalBirds, reserved, approved, openSlots
 */
export const getHandlerCapacitySummary = (handler, allRequests = []) => {
  if (!handler?.handlerInfo) {
    return {
      totalBirds: 0,
      reserved: 0,
      approved: 0,
      openSlots: 0,
      isAtCapacity: true
    };
  }

  const totalBirds = handler.handlerInfo.totalBirds || 0;
  const reserved = handler.handlerInfo.reserved || 0;
  const openSlots = calculateHandlerOpenSlots(handler, allRequests);
  const approved = totalBirds - reserved - openSlots;

  return {
    totalBirds,
    reserved,
    approved,
    openSlots,
    isAtCapacity: openSlots <= 0
  };
};